import { useEffect, useState } from 'react'
import { nf } from '@/shared/util/numbers'
import { InfoTip } from '@/shared/ui/InfoTip'
import { Flag, LanguageFlag } from '@/shared/places/Flag'
import { LANGUAGES, LANGUAGE_NAMES, countryName, isLanguage } from '@/shared/places/places'
import { loadPlaceDistribution, type PlaceDistribution } from '@/shared/places/customerFacts'
import { CATEGORICAL, DonutChart, OTHER } from '@/shared/charts/DonutChart'
import { WorldMap } from '@/shared/charts/WorldMap'
import './PlacesCard.css'

/** Where the map moves beside the pies. Mirrored in PlacesCard.css. */
const WIDE = '(min-width: 1100px)'

function useMediaQuery(q: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches)
  useEffect(() => {
    const m = window.matchMedia(q)
    const onChange = () => setOn(m.matches)
    m.addEventListener('change', onChange)
    return () => m.removeEventListener('change', onChange)
  }, [q])
  return on
}

/** The countries the pie names; the rest are one "Other" slice. */
const TOP_COUNTRIES = 5

/**
 * Where the app's users are, and in which language Skida writes to them —
 * 28 Oct 2026.
 *
 * The totals the engine keeps when it reads RevenueCat every six hours: the
 * country each account was last seen in, and the language that country gets.
 * The same figures newsletters are filtered on.
 *
 * In three parts since 29 Oct 2026, as the owner asked: languages as a pie,
 * countries as a pie of the five largest and the rest, and a map shaded by
 * how many users each country has. On a wide screen the pies stack on the
 * left and the map fills the rest, as tall as the two of them.
 */
export function PlacesCard() {
  const [d, setD] = useState<PlaceDistribution | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'none' | 'failed'>('loading')
  // Beside the stacked pies on a wide screen, beneath them otherwise —
  // the same breakpoint as `.places__three` in PlacesCard.css.
  const wide = useMediaQuery(WIDE)

  useEffect(() => {
    let live = true
    loadPlaceDistribution()
      .then((x) => {
        if (!live) return
        setD(x)
        setState(x ? 'ok' : 'none')
      })
      .catch(() => live && setState('failed'))
    return () => {
      live = false
    }
  }, [])

  /** Users, or paying subscribers — 29 Oct 2026. Pies and map switch together. */
  const [who, setWho] = useState<'users' | 'subscribers'>('users')
  const shown = who === 'subscribers' ? d?.subscribers ?? null : d
  const languages = Object.entries(shown?.languages ?? {}).sort((a, b) => b[1] - a[1])
  const countries = Object.entries(shown?.countries ?? {}).sort((a, b) => b[1] - a[1])
  const rest = countries.slice(TOP_COUNTRIES).reduce((n, [, v]) => n + v, 0)

  return (
    <section className="card places">
      <div className="card-label card-label--row">
        <span>
          Languages and countries
          <InfoTip label="Languages and countries">
            Where RevenueCat last saw each account, and the language Skida writes in there — Norway
            Norwegian, Sweden Swedish, Germany, Austria and Switzerland German, France French, Italy
            Italian, Spain Spanish, everywhere else English. Read every six hours. Newsletters can be
            sent to, and sections shown to, a language or a country.
          </InfoTip>
        </span>
        <span className="places__toggle">
          <button className="chip" aria-pressed={who === 'users'} onClick={() => setWho('users')}>
            Users
          </button>
          <button
            className="chip"
            aria-pressed={who === 'subscribers'}
            disabled={state === 'ok' && !d?.subscribers}
            title={
              d?.subscribers
                ? 'Paying subscribers (App Store and Google Play). Not trials, not Skida PRO.'
                : 'Counted from the next read of RevenueCat, within six hours.'
            }
            onClick={() => setWho('subscribers')}
          >
            Subscribers
          </button>
          {d?.at && <span className="micro muted">Read {d.at.toLocaleString('nb-NO')}</span>}
        </span>
      </div>

      {state === 'loading' && <p className="empty-note">Reading…</p>}
      {state === 'failed' && <p className="empty-note">Could not read the figures.</p>}
      {state === 'none' && <p className="empty-note">Not read from RevenueCat yet.</p>}

      {state === 'ok' && d && (
        <>
          <p className="micro muted places__sum">
            {who === 'users'
              ? `${nf(d.withCountry)} of ${nf(d.seen)} accounts with a known country`
              : `${nf(shown?.withCountry ?? 0)} of ${nf(shown?.seen ?? 0)} paying subscribers with a known country — App Store and Google Play, not trials or Skida PRO`}
          </p>
          <div className="places__three">
            <DonutChart
              title="Language"
              slices={languages.map(([code, n]) => ({
                key: code,
                label: isLanguage(code) ? LANGUAGE_NAMES[code] : code,
                value: n,
                // Colour follows the language, not its rank: Norsk is always blue.
                color: isLanguage(code) ? CATEGORICAL[LANGUAGES.indexOf(code)]! : OTHER,
                icon: <LanguageFlag language={code} />,
              }))}
            />
            <DonutChart
              title="Country — the five largest"
              slices={[
                ...countries.slice(0, TOP_COUNTRIES).map(([code, n], i) => ({
                  key: code,
                  label: countryName(code),
                  value: n,
                  color: CATEGORICAL[i]!,
                  icon: <Flag code={code} />,
                })),
                ...(rest > 0
                  ? [
                      {
                        key: 'other',
                        label: `Other (${countries.length - TOP_COUNTRIES} countries)`,
                        value: rest,
                        color: OTHER,
                        icon: <span className="cflag cflag--none">+</span>,
                      },
                    ]
                  : []),
              ]}
            />
            <div className="places__map">
              <div className="places__head">Where they are</div>
              <WorldMap
                counts={shown?.countries ?? {}}
                total={shown?.withCountry ?? 0}
                fill={wide}
                noun={who === 'users' ? 'users' : 'subscribers'}
              />
            </div>
          </div>
        </>
      )}
    </section>
  )
}
